import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { MainLayout } from '../../components/layout';
import Card from '../../components/ui/Card';
import Button from '../../components/ui/Button';
import Table from '../../components/ui/Table';
import Modal from '../../components/ui/Modal';
import Input from '../../components/ui/Input';
import Badge from '../../components/ui/Badge';
import Loading from '../../components/ui/Loading';
import Alert from '../../components/ui/Alert';
import Tabs from '../../components/ui/Tabs';
import { Plus, Building2, Edit2, ToggleLeft, ToggleRight, Search, Eye, Users, UserCircle, Shield, MapPin } from 'lucide-react';
import { tenantService } from '../../services';
import { useImpersonation } from '../../contexts/ImpersonationContext';
import type { Tenant } from '../../types';
import UsersManagementTab from './tabs/UsersManagementTab';
import RolesPermissionsTab from './tabs/RolesPermissionsTab';
import LocationAssignmentsTab from './tabs/LocationAssignmentsTab';

const TenantsPage: React.FC = () => {
  const [activeTab, setActiveTab] = useState('tenants');
  const navigate = useNavigate();
  const { startImpersonation, isImpersonating, isLoading: isImpersonationLoading } = useImpersonation();
  const [tenants, setTenants] = useState<Tenant[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [showInactive, setShowInactive] = useState(false);
  const [isStatusModalOpen, setIsStatusModalOpen] = useState(false);
  const [selectedTenant, setSelectedTenant] = useState<Tenant | null>(null);

  useEffect(() => {
    fetchTenants();
  }, [showInactive]);

  const fetchTenants = async () => {
    try {
      setIsLoading(true);
      setError(null);
      // Always include inactive tenants so we can see and manage their status
      const response = await tenantService.getTenants({ include_inactive: true });
      setTenants(response.items || []);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch tenants');
    } finally {
      setIsLoading(false);
    }
  };

  const handleToggleStatus = async () => {
    if (!selectedTenant) return;

    try {
      // API expects status as 'Y' or 'N' string
      const isCurrentlyActive = selectedTenant.status === true || (selectedTenant as any).status === 'Y';
      const newStatus = isCurrentlyActive ? 'N' : 'Y';
      await tenantService.updateTenant(selectedTenant.company_id, { status: newStatus });
      setIsStatusModalOpen(false);
      setSelectedTenant(null);
      fetchTenants();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update tenant status');
    }
  };

  const openStatusModal = (tenant: Tenant) => {
    setSelectedTenant(tenant);
    setIsStatusModalOpen(true);
  };

  // Filter tenants based on search term
  const filteredTenants = tenants.filter(tenant =>
    tenant.company_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    (tenant.company_code?.toLowerCase() || '').includes(searchTerm.toLowerCase()) ||
    (tenant.email?.toLowerCase() || '').includes(searchTerm.toLowerCase())
  );

  // Get indent based on level
  const getIndent = (level: number) => {
    if (level <= 1) return '';
    return '└─ '.padStart((level - 1) * 4 + 3, '  ');
  };

  const columns = [
    {
      key: 'company_name',
      label: 'Tenant',
      render: (tenant: Tenant) => (
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 bg-accent-primary/10 rounded-lg flex items-center justify-center">
            <Building2 size={16} className="text-accent-primary" />
          </div>
          <div>
            <div className="font-medium text-text-primary">
              <span className="text-text-muted font-mono text-xs">{getIndent(tenant.level)}</span>
              {tenant.company_name}
            </div>
            <div className="text-xs text-text-muted">{tenant.company_code || 'N/A'}</div>
          </div>
        </div>
      ),
    },
    {
      key: 'parent_company',
      label: 'Service Provider',
      render: (tenant: Tenant) => (
        <span className="text-text-secondary">
          {tenant.parent_company_name || '—'}
        </span>
      ),
    },
    {
      key: 'level',
      label: 'Level',
      render: (tenant: Tenant) => (
        <Badge variant="default">
          Level {tenant.level}
        </Badge>
      ),
    },
    {
      key: 'email',
      label: 'Email',
      render: (tenant: Tenant) => (
        <span className="text-text-secondary">{tenant.email || 'N/A'}</span>
      ),
    },
    {
      key: 'phone',
      label: 'Phone',
      render: (tenant: Tenant) => (
        <span className="text-text-secondary">{tenant.phone || 'N/A'}</span>
      ),
    },
    {
      key: 'status',
      label: 'Status',
      render: (tenant: Tenant) => {
        const isActive = tenant.status === true || (tenant as any).status === 'Y';
        return (
          <Badge variant={isActive ? 'success' : 'danger'}>
            {isActive ? 'Active' : 'Inactive'}
          </Badge>
        );
      },
    },
    {
      key: 'actions',
      label: 'Actions',
      render: (tenant: Tenant) => {
        const isActive = tenant.status === true || (tenant as any).status === 'Y';
        return (
          <div className="flex items-center gap-2">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => navigate(`/tenants/${tenant.company_id}/view`)}
              title="View"
            >
              <Eye size={16} />
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => navigate(`/tenants/${tenant.company_id}/edit`)}
              title="Edit"
            >
              <Edit2 size={16} />
            </Button>
            {isActive && !isImpersonating && (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => startImpersonation(tenant.company_id, tenant.company_name)}
                className="text-accent-primary hover:bg-accent-primary/10"
                title="Impersonate"
                disabled={isImpersonationLoading}
              >
                <UserCircle size={16} />
              </Button>
            )}
            <Button
              variant="ghost"
              size="sm"
              onClick={() => openStatusModal(tenant)}
              className={isActive ? 'text-status-warning hover:bg-status-warning/10' : 'text-status-success hover:bg-status-success/10'}
              title={isActive ? 'Deactivate' : 'Activate'}
            >
              {isActive ? <ToggleRight size={16} /> : <ToggleLeft size={16} />}
            </Button>
          </div>
        );
      },
    },
  ];

  const tabs = [
    { id: 'tenants', label: 'Tenants', icon: <Building2 size={18} />, count: tenants.length },
    { id: 'users', label: 'Users', icon: <Users size={18} /> },
    { id: 'roles', label: 'Roles & Permissions', icon: <Shield size={18} /> },
    { id: 'locations', label: 'Location Admins', icon: <MapPin size={18} /> },
  ];

  return (
    <MainLayout>
      <div className="page-stack">
        {/* Header */}
        <div className="page-header">
          <div>
            <h1 className="page-title">Tenant Management</h1>
            <p className="page-description">
              {activeTab === 'tenants' && 'Manage your sub-tenants and child companies'}
              {activeTab === 'users' && 'Manage user accounts and roles'}
              {activeTab === 'roles' && 'Configure roles and screen permissions'}
              {activeTab === 'locations' && 'Assign location admins to manage specific locations'}
            </p>
          </div>
          {activeTab === 'tenants' && (
            <Button onClick={() => navigate('/tenants/create')}>
              <Plus size={18} className="mr-2" />
              Add Tenant
            </Button>
          )}
        </div>

        {/* Tabs */}
        <Card>
          <Tabs tabs={tabs} activeTab={activeTab} onChange={setActiveTab} />
        </Card>

        {error && (
          <Alert variant="error" message={error} onClose={() => setError(null)} />
        )}

        {/* Tab Content */}
        {activeTab === 'tenants' && (
          <>
            {/* Filters Card */}
            <Card>
              <div className="p-4">
                <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center justify-between">
                  <div className="relative flex-1 max-w-md">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-text-muted" size={18} />
                    <Input
                      type="text"
                      placeholder="Search tenants..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="pl-10"
                      fullWidth
                    />
                  </div>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={showInactive}
                      onChange={(e) => setShowInactive(e.target.checked)}
                      className="w-4 h-4 rounded border-gray-300 text-accent-primary focus:ring-accent-primary"
                    />
                    <span className="text-sm text-text-secondary">Show inactive</span>
                  </label>
                </div>
              </div>
            </Card>

            {/* Tenants Table */}
            <Card>
              {isLoading ? (
                <div className="flex items-center justify-center p-12">
                  <Loading size="lg" />
                </div>
              ) : filteredTenants.length === 0 ? (
                <div className="flex flex-col items-center justify-center p-12 text-center">
                  <div className="w-16 h-16 bg-surface-secondary rounded-full flex items-center justify-center mb-4">
                    <Users size={32} className="text-text-muted" />
                  </div>
                  <h3 className="text-lg font-medium text-text-primary mb-2">No tenants found</h3>
                  <p className="text-text-muted mb-4">
                    {searchTerm ? 'Try adjusting your search' : 'Get started by adding your first tenant'}
                  </p>
                  {!searchTerm && (
                    <Button onClick={() => navigate('/tenants/create')}>
                      <Plus size={18} className="mr-2" />
                      Add Tenant
                    </Button>
                  )}
                </div>
              ) : (
                <Table columns={columns} data={filteredTenants} />
              )}
            </Card>
          </>
        )}

        {activeTab === 'users' && <UsersManagementTab />}
        {activeTab === 'roles' && <RolesPermissionsTab />}
        {activeTab === 'locations' && <LocationAssignmentsTab />}

        {/* Status Toggle Confirmation Modal */}
        {(() => {
          const isActive = selectedTenant?.status === true || (selectedTenant as any)?.status === 'Y';
          return (
            <Modal
              isOpen={isStatusModalOpen}
              onClose={() => setIsStatusModalOpen(false)}
              title={isActive ? 'Deactivate Tenant' : 'Activate Tenant'}
            >
              <div className="space-y-4">
                <p className="text-text-secondary">
                  Are you sure you want to {isActive ? 'deactivate' : 'activate'} <strong>{selectedTenant?.company_name}</strong>?
                  {isActive && ' The tenant will no longer be able to access the system.'}
                </p>
                <div className="flex justify-end gap-3">
                  <Button variant="secondary" onClick={() => setIsStatusModalOpen(false)}>
                    Cancel
                  </Button>
                  <Button 
                    variant={isActive ? 'danger' : 'primary'} 
                    onClick={handleToggleStatus}
                  >
                    {isActive ? 'Deactivate' : 'Activate'}
                  </Button>
                </div>
              </div>
            </Modal>
          );
        })()}
      </div>
    </MainLayout>
  );
};

export default TenantsPage;
